class SwyftTabs extends HTMLElement {
  constructor() {
    super();
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.navItems = [];
    this.panes = [];
  }

  connectedCallback() {
    const navList = this.querySelector('.tabs__nav');
    if (navList) {
      navList.setAttribute('role', 'tablist');
    }
    this.navItems = Array.from(this.querySelectorAll('[data-tab-target]'));
    this.panes = Array.from(this.querySelectorAll('.tabs__pane'));

    this.navItems.forEach((item) => {
      item.setAttribute('role', 'tab');
      item.setAttribute('tabindex', item.classList.contains('tabs__nav-item--active') ? '0' : '-1');
      const targetId = item.dataset.tabTarget;
      if (targetId) {
        item.setAttribute('aria-controls', targetId);
        const pane = this.querySelector(`#${targetId}`);
        if (pane) {
          const fallbackId = `${this.id || 'swyft-tabs'}-tab-${this.navItems.indexOf(item) + 1}`;
          if (!item.id) {
            item.id = fallbackId;
          }
          pane.setAttribute('role', 'tabpanel');
          pane.setAttribute('aria-labelledby', item.id);
        }
      }
      item.addEventListener('click', this.handleTabClick);
      item.addEventListener('keydown', this.handleKeydown);
    });

    this.panes.forEach((pane) => {
      if (!pane.hasAttribute('role')) {
        pane.setAttribute('role', 'tabpanel');
      }
    });

    this.initialiseActiveState();
  }

  disconnectedCallback() {
    this.navItems.forEach((item) => {
      item.removeEventListener('click', this.handleTabClick);
      item.removeEventListener('keydown', this.handleKeydown);
    });
  }

  initialiseActiveState() {
    const activeId = this.getActiveTabId();
    if (activeId) {
      this.activateTab(activeId, { focus: false });
      return;
    }

    if (this.navItems.length > 0) {
      this.activateTab(this.navItems[0].dataset.tabTarget, { focus: false });
    }
  }

  getActiveTabId() {
    const activeNav = this.navItems.find((item) => item.classList.contains('tabs__nav-item--active'));
    return activeNav ? activeNav.dataset.tabTarget : null;
  }

  handleTabClick(event) {
    event.preventDefault();
    const targetId = event.currentTarget.dataset.tabTarget;
    this.activateTab(targetId);
  }

  handleKeydown(event) {
    const item = event.currentTarget;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const currentIndex = this.navItems.indexOf(item);
    let newIndex = currentIndex;

    if (event.key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + this.navItems.length) % this.navItems.length;
    } else if (event.key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % this.navItems.length;
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = this.navItems.length - 1;
    }

    const newTarget = this.navItems[newIndex];
    if (newTarget) {
      this.activateTab(newTarget.dataset.tabTarget, { focus: true });
    }
  }

  activateTab(targetId, { focus = false } = {}) {
    if (!targetId) {
      return;
    }

    const navItem = this.navItems.find((item) => item.dataset.tabTarget === targetId);
    const pane = this.panes.find((item) => item.id === targetId);

    if (!navItem || !pane) {
      return;
    }

    this.navItems.forEach((item) => {
      item.classList.toggle('tabs__nav-item--active', item === navItem);
      item.setAttribute('tabindex', item === navItem ? '0' : '-1');
      item.setAttribute('aria-selected', item === navItem ? 'true' : 'false');
    });

    this.panes.forEach((item) => {
      item.classList.toggle('tabs__pane--active', item === pane);
      item.setAttribute('aria-hidden', item === pane ? 'false' : 'true');
    });

    if (focus) {
      navItem.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('swyft-tabs')) {
  customElements.define('swyft-tabs', SwyftTabs);
}

function findTabComponent(selector) {
  if (!selector) {
    return document.querySelector('swyft-tabs');
  }

  try {
    const component = document.querySelector(selector);
    if (component instanceof SwyftTabs) {
      return component;
    }
    if (component && component.tagName && component.tagName.toLowerCase() === 'swyft-tabs') {
      return component;
    }
  } catch (error) {
    console.warn('Invalid selector provided for data-tabs-root:', selector, error);
  }

  return null;
}

function resolveScrollTarget(trigger, component) {
  const selector = trigger.getAttribute('data-scroll-target');
  if (selector) {
    try {
      const match = document.querySelector(selector);
      if (match) {
        return match;
      }
    } catch (error) {
      console.warn('Invalid selector provided for data-scroll-target:', selector, error);
    }
  }

  const sectionWrapper = component ? component.closest('[id^="shopify-section-"]') : null;
  if (sectionWrapper) {
    return sectionWrapper;
  }

  return component;
}

function getScrollOffset(trigger) {
  const offsetAttr = trigger.getAttribute('data-scroll-offset');
  if (!offsetAttr) {
    return 0;
  }

  const parsed = parseInt(offsetAttr, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function scrollToTarget(target, options) {
  if (!target || typeof target.getBoundingClientRect !== 'function') {
    return;
  }

  const { behavior = 'smooth', offset = 0 } = options || {};
  const rect = target.getBoundingClientRect();
  const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
  const destination = rect.top + currentScroll - offset;

  window.scrollTo({
    top: destination,
    behavior,
  });
}

function handleExternalTabTrigger(event) {
  const trigger = event.target.closest('[data-open-tab-target]');
  if (!trigger) {
    return;
  }

  const tabId = trigger.getAttribute('data-open-tab-target');
  if (!tabId) {
    return;
  }

  const component = findTabComponent(trigger.getAttribute('data-tabs-root'));
  if (!component || typeof component.activateTab !== 'function') {
    return;
  }

  event.preventDefault();

  component.activateTab(tabId, {
    focus: trigger.getAttribute('data-tab-focus') !== 'false',
  });

  const scrollTarget = resolveScrollTarget(trigger, component);
  const behavior = trigger.getAttribute('data-scroll-behavior') || 'smooth';
  const offset = getScrollOffset(trigger);
  scrollToTarget(scrollTarget, { behavior, offset });
}

document.addEventListener('click', handleExternalTabTrigger);